import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, LogOut, Lock, Users, Car, KeyRound, LayoutDashboard, KanbanSquare, Wallet, PiggyBank, CalendarClock, UserRound, Wrench, Megaphone } from "lucide-react";
import { Logo } from "../components/Logo";
import { LeadsTab } from "../components/admin/LeadsTab";
import { VehiclesTab } from "../components/admin/VehiclesTab";
import { AccountTab } from "../components/admin/AccountTab";
import { DashboardTab } from "../components/admin/DashboardTab";
import { FunilTab } from "../components/admin/FunilTab";
import { FinanceiroTab } from "../components/admin/FinanceiroTab";
import { CustosTab } from "../components/admin/CustosTab";
import { LocacaoTab } from "../components/admin/LocacaoTab";
import { VendedoresTab } from "../components/admin/VendedoresTab";
import { ManutencaoTab } from "../components/admin/ManutencaoTab";
import { AnunciosTab } from "../components/admin/AnunciosTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tabs = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "funil", label: "Funil", icon: KanbanSquare },
  { id: "estoque", label: "Estoque", icon: Car },
  { id: "anuncios", label: "Anúncios", icon: Megaphone },
  { id: "custos", label: "Custos", icon: PiggyBank, adminOnly: true },
  { id: "locacao", label: "Locação", icon: CalendarClock },
  { id: "manutencao", label: "Manutenção", icon: Wrench },
  { id: "financeiro", label: "Financeiro", icon: Wallet, adminOnly: true },
  { id: "equipe", label: "Equipe", icon: UserRound },
  { id: "leads", label: "Leads", icon: Users },
  { id: "conta", label: "Conta", icon: KeyRound },
];

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("seed_admin_token"));
  const [role, setRole] = useState(() => localStorage.getItem("seed_admin_role") || "admin");
  const [tab, setTab] = useState("dashboard");
  const [anuncioPrefill, setAnuncioPrefill] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const ml = p.get("ml");
    if (ml === "connected") {
      toast.success("Conta do Mercado Livre conectada! Já pode publicar veículos pelo Estoque.");
      setTab("conta");
    } else if (ml === "error") {
      toast.error("Não foi possível conectar ao Mercado Livre. Verifique o cadastro do app no DevCenter e tente de novo.");
      setTab("conta");
    }
    if (ml) window.history.replaceState({}, "", "/admin");
  }, []);

  const tabsVisiveis = tabs.filter((t) => !t.adminOnly || role === "admin");

  const gerarAnuncioDe = (v) => {
    setAnuncioPrefill(v);
    setTab("anuncios");
  };

  const onUnauthorized = () => {
    localStorage.removeItem("seed_admin_token");
    localStorage.removeItem("seed_admin_role");
    setToken(null);
  };

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("seed_admin_token", res.data.token);
      localStorage.setItem("seed_admin_role", res.data.role || "admin");
      setRole(res.data.role || "admin");
      setToken(res.data.token);
      toast.success(`Bem-vindo(a), ${res.data.nome || "equipe"}!`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha no login.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("seed_admin_token");
    localStorage.removeItem("seed_admin_role");
    setToken(null);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-[#07090B]" data-testid="admin-login-page">
        <motion.form
          onSubmit={login}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="glass rounded-3xl p-10 w-full max-w-md space-y-5"
          data-testid="admin-login-form"
        >
          <a href="/" className="inline-block group"><Logo /></a>
          <div>
            <h1 className="font-display text-2xl font-bold text-white flex items-center gap-2">
              <Lock className="w-5 h-5 text-emerald-400" /> Painel do lojista
            </h1>
            <p className="text-sm text-slate-400 mt-1">Acesso restrito à administração.</p>
          </div>
          <input
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="E-mail de administrador" data-testid="admin-input-email"
            className="w-full bg-[#0d0f17] border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none transition-colors"
          />
          <input
            type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Senha" data-testid="admin-input-password"
            className="w-full bg-[#0d0f17] border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none transition-colors"
          />
          <button
            type="submit" disabled={loading} data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-emerald-950 font-bold px-6 py-4 rounded-full transition-colors"
          >
            {loading && <Loader2 className="w-5 h-5 animate-spin" />}
            Entrar no painel
          </button>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#07090B]" data-testid="admin-panel">
      <header className="glass border-b border-slate-800/60 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <a href="/" className="group"><Logo /></a>
          <button
            onClick={logout} data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-sm font-semibold px-4 py-2.5 rounded-full transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sair
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap gap-2 mb-10" data-testid="admin-tabs">
          {tabsVisiveis.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-300 ${
                tab === t.id ? "bg-emerald-500 text-emerald-950" : "glass text-slate-300 hover:text-emerald-400"
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        {tab === "dashboard" && <DashboardTab onUnauthorized={onUnauthorized} />}
        {tab === "funil" && <FunilTab onUnauthorized={onUnauthorized} />}
        {tab === "estoque" && <VehiclesTab onUnauthorized={onUnauthorized} role={role} onGerarAnuncio={gerarAnuncioDe} />}
        {tab === "anuncios" && <AnunciosTab onUnauthorized={onUnauthorized} prefill={anuncioPrefill} onPrefillConsumed={() => setAnuncioPrefill(null)} />}
        {tab === "custos" && role === "admin" && <CustosTab onUnauthorized={onUnauthorized} />}
        {tab === "locacao" && <LocacaoTab onUnauthorized={onUnauthorized} />}
        {tab === "manutencao" && <ManutencaoTab onUnauthorized={onUnauthorized} />}
        {tab === "financeiro" && role === "admin" && <FinanceiroTab onUnauthorized={onUnauthorized} />}
        {tab === "equipe" && <VendedoresTab onUnauthorized={onUnauthorized} />}
        {tab === "leads" && <LeadsTab onUnauthorized={onUnauthorized} />}
        {tab === "conta" && <AccountTab onUnauthorized={onUnauthorized} role={role} />}
      </main>
    </div>
  );
}
